import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle, Clock, Globe, FileText, Search, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ANALYSIS_STEPS = [
  { id: 'website', label: 'Scanning website structure', icon: Globe },
  { id: 'robots', label: 'Analyzing robots.txt', icon: FileText },
  { id: 'content', label: 'Extracting content', icon: Search },
  { id: 'niche', label: 'Detecting business niche', icon: Zap },
  { id: 'keywords', label: 'Identifying keywords', icon: Search },
  { id: 'complete', label: 'Analysis complete', icon: CheckCircle }
];

const STATUS_MESSAGES = [
  "Gathering comprehensive website data...",
  "Analyzing your business model...",
  "Detecting industry-specific keywords...",
  "Identifying content opportunities...",
  "Building your SEO profile...",
  "Preparing personalized insights..."
];

export default function AnalysisStatus() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState(STATUS_MESSAGES[0]);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisStartTime] = useState(Date.now());
  const navigate = useNavigate();

  // Redirect if no websiteId
  useEffect(() => {
    if (!websiteId) {
      navigate('/dashboard');
      return;
    }
  }, [websiteId, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < ANALYSIS_STEPS.length - 1) {
          return prev + 1;
        }
        return prev;
      });
      
      setProgress((prev) => {
        if (prev < 90) {
          return prev + Math.random() * 15;
        }
        return prev;
      });

      setStatusMessage((prev) => {
        const currentIndex = STATUS_MESSAGES.indexOf(prev);
        const nextIndex = (currentIndex + 1) % STATUS_MESSAGES.length;
        return STATUS_MESSAGES[nextIndex];
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!websiteId) return;

    const checkAnalysisStatus = async () => {
      try {
        const { data: website } = await supabase
          .from('websites')
          .select('analysis_status, analysis_completed_at')
          .eq('id', websiteId)
          .single();

        if (website?.analysis_status === 'completed') {
          setProgress(100);
          setCurrentStep(ANALYSIS_STEPS.length - 1);
          setStatusMessage("Analysis complete! Redirecting to dashboard...");
          setAnalysisComplete(true);
          
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking analysis status:', error);
      }
    };

    const statusInterval = setInterval(checkAnalysisStatus, 5000);
    checkAnalysisStatus();

    return () => clearInterval(statusInterval);
  }, [websiteId, navigate]);

  const timeElapsed = Math.floor((Date.now() - analysisStartTime) / 1000);
  const isOverTime = timeElapsed > 3600; // 1 hour

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8 text-center space-y-8">
        <div className="space-y-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <Search className="w-8 h-8 text-primary animate-pulse" />
          </div>
          
          <h1 className="text-3xl font-bold">Analyzing Your Website</h1>
          <p className="text-muted-foreground text-lg">
            We're gathering comprehensive data about your business to create personalized SEO strategies
          </p>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{statusMessage}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          <div className="grid gap-3">
            {ANALYSIS_STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index <= currentStep;
              const isCompleted = index < currentStep || analysisComplete;
              
              return (
                <div 
                  key={step.id} 
                  className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                    isActive ? 'bg-primary/5 border border-primary/20' : 'bg-muted/30'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isCompleted ? 'bg-primary text-primary-foreground' : 
                    isActive ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className={isActive ? 'font-medium' : 'text-muted-foreground'}>
                    {step.label}
                  </span>
                  {isActive && !isCompleted && (
                    <Clock className="w-4 h-4 text-primary animate-spin ml-auto" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Time elapsed: {Math.floor(timeElapsed / 60)}m {timeElapsed % 60}s
          </div>
          
          {isOverTime && (
            <div className="space-y-3">
              <p className="text-sm text-amber-600">
                Analysis is taking longer than expected.
              </p>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Continue to Dashboard
              </Button>
              <p className="text-xs text-muted-foreground">
                If data isn't available within 1 hour, please contact support.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}